import { Injectable } from '@angular/core';
import { AppService } from '@app/app.service';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { DialogRef } from '@app/shared/dialog/dialog-ref';
import { DialogService } from '@app/shared/dialog/dialog.service';
import { QuickCommandsDialogComponent } from '@app/shared/quick-commands/components/quick-commands-dialog/quick-commands-dialog.component';
import { AuthorizationService } from '@app/shared/services/authorization.service';
import { HotkeyService } from '@app/shared/services/hotkey.service';
import { RecentlyUsedService } from '@app/shared/services/recently-used.service';
import { registerWithGlobalApp } from '@app/utils/app';
import { groupBy, withoutEntry } from '@app/utils/common';
import { portalError, throwPortalError } from '@app/utils/error/portal-error';
import { filter$, isTrue } from '@app/utils/rxjs';
import {
  BehaviorSubject,
  combineLatest,
  forkJoin,
  from,
  isObservable,
  Observable,
  of,
  Subscription,
} from 'rxjs';
import { first, map, mergeMap, toArray } from 'rxjs/operators';
import {
  Command,
  CommandDefinition,
  CommandResult,
  CommandResultEntry,
  CommandResultList,
  CommandResults,
  CommandSection,
  CommandStatusbar,
  EnhancedCommandResultEntry,
} from '.';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class QuickCommandService {
  public static readonly CurrentSection: CommandSection = {
    label: 'quickCommands.sections.current',
    name: 'current',
  };

  public static readonly DevSection: CommandSection = {
    label: 'quickCommands.sections.dev',
    name: 'dev',
  };

  public static readonly HistorySection: CommandSection = {
    group: false,
    label: 'quickCommands.sections.history',
    name: 'history',
  };

  private readonly defaultSections: CommandSection[] = [{ label: '', name: 'others' }];

  private dialogRef: DialogRef<any, any, any> | null = null;
  private hotkeySubscription: Subscription;

  private allCommands$ = new BehaviorSubject<CommandDefinition<unknown>[]>([]);
  private availableCommands$: Observable<CommandDefinition<unknown>[]>;

  get commands$(): Observable<{
    commands: Command[];
    sections: CommandSection[];
  }> {
    return this.availableCommands$.pipe(
      map((availableCommands) => {
        return {
          commands: this.getCommands(availableCommands),
          sections: this.getSections(availableCommands),
        };
      })
    );
  }

  constructor(
    appService: AppService,
    private authenticationService: AuthenticationService,
    private authorizationService: AuthorizationService,
    private dialogService: DialogService,
    private hotkeyService: HotkeyService,
    private recentlyUsedService: RecentlyUsedService
  ) {
    appService.initialized$.pipe(isTrue(), first()).subscribe(() => {
      this.initialize();
    });

    this.availableCommands$ = this.allCommands$.pipe(
      mergeMap((commands) => {
        const { noAuth = of([]), requiresAuth = of([]) } = groupBy(
          commands,
          (command) => (command.authorization != null ? 'requiresAuth' : 'noAuth'),
          (commands, key) => (key === 'requiresAuth' ? this.filterByAuthorized$(commands) : of(commands))
        );

        return forkJoin([noAuth, requiresAuth]).pipe(map((values) => values.flat()));
      })
    );

    if (environment.production === false) {
      registerWithGlobalApp('quickCommands', this);
    }
  }

  /**
   * Add a new command.
   *
   * @param command Command definition.
   * @return Teardown function that removes the command when called.
   */
  add(command: CommandDefinition<unknown>): () => void {
    this.allCommands$.pipe(first()).subscribe((commands) => {
      const alreadyExists = commands.some((_) => _.name === command.name);

      if (alreadyExists) {
        throw portalError({
          cause: new Error(`Command with this name already exists. command=${command.name}`),
          id: 'quick-command-service:add',
        });
      }

      this.allCommands$.next([...commands, command]);
    });

    return () => {
      this.remove(command);
    };
  }

  /**
   * Calls the given command.
   *
   * @param command Command to call.
   * @param query A string entered by the user to filter/modify the results of the command.
   * @param results Results selected by the user.
   * @param remember Whether to remember the last result.
   * @return Result returned by the command.
   */
  call$(
    command: Command,
    { query, remember, results }: CallCommandOptions
  ): Observable<CommandResult<unknown>> {
    return combineLatest([this.availableCommands$, this.allCommands$]).pipe(
      first(),
      mergeMap(([availableCommands, allCommands]) => {
        let commandDefinition = availableCommands.find(
          (commandDefinition) => commandDefinition.name === command.name
        );

        if (commandDefinition == null) {
          const commandDefinition = allCommands.find((_) => _.name === command.name);

          if (commandDefinition != null) {
            return throwPortalError({
              cause: new Error('Not allowed to call this command.'),
              id: 'quick-command-service:call',
            });
          } else {
            return throwPortalError({
              cause: new Error(`Cannot find command. command=${command.name}`),
              id: 'quick-command-service:unknown-command',
            });
          }
        }

        const values = results.map((result) => result.value);

        if (remember) {
          const activeResults = results.slice(0, -1);
          const lastResult = results.at(-1);

          this.rememberCommandResult(command, activeResults, lastResult);
        }

        // Commands can now return observables as results, and we
        // have to account for that. That's why we remember the first
        // result, so it's possible to handle subsequent results.
        let firstResult = true;

        return wrap(commandDefinition.action(query, values)).pipe(
          map((result) => {
            const commandResult = this.mapCommandResult(
              result,
              command,
              commandDefinition,
              results,
              firstResult
            );

            firstResult = false;

            return commandResult;
          })
        );
      })
    );
  }

  /**
   * Open the quick command dialog.
   */
  open(): void {
    if (this.dialogRef != null) {
      this.dialogRef.dismiss();
    }

    this.dialogRef = this.dialogService
      .open(QuickCommandsDialogComponent)
      .onClose(() => {
        this.dialogRef = null;
      })
      .onDismiss(() => {
        return (this.dialogRef = null);
      });
  }

  removeHistory(commandName: string, activeResults: string[], idToRemove: string): void;
  removeHistory(command: Command, activeResults: CommandResultEntry[], entry: CommandResultEntry): void;
  removeHistory(
    commandName: Command | string,
    activeResults: (CommandResultEntry | string)[],
    entryOrId: CommandResultEntry | string
  ): void {
    const historyName = this.getHistoryName(commandName, activeResults);
    const historyId = typeof entryOrId === 'string' ? entryOrId : entryOrId.historyId;

    this.recentlyUsedService.remove(historyName, historyId);
  }

  /**
   * Remove the given command.
   *
   * @param command Command definition object previously added.
   */
  remove(command: CommandDefinition<unknown>): void {
    this.allCommands$.pipe(first()).subscribe((commands) => {
      this.allCommands$.next(withoutEntry(commands, command));
    });
  }

  private filterByAuthorized$(
    commands: CommandDefinition<unknown>[]
  ): Observable<CommandDefinition<unknown>[]> {
    return from(commands).pipe(
      filter$((command) => this.authorizationService.isAuthorized$(command.authorization).pipe(first())),
      toArray()
    );
  }

  private getLabel(command: CommandDefinition<unknown>) {
    let label = command.label;

    if (typeof label === 'function') {
      label = label();
    }

    return label;
  }

  private initialize(): void {
    this.authenticationService.isAuthenticated$.subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        if (this.hotkeySubscription == null) {
          this.hotkeySubscription = this.hotkeyService
            .addShortcut({
              ignore: () => this.dialogService.hasOpenModals(),
              keys: 'control.k',
              prevent: true,
              stop: true,
            })
            .subscribe(() => {
              this.open();
            });
        }
      } else {
        this.hotkeySubscription?.unsubscribe();
        this.hotkeySubscription = null;
      }
    });
  }

  private mapCommandResult(
    result: CommandResults<unknown>,
    command: Command,
    commandDefinition: CommandDefinition<unknown>,
    activeResults: EnhancedCommandResultEntry[],
    firstResult: boolean
  ): CommandResult<unknown> {
    if (result.type === 'list') {
      let sections = result.sections ?? this.defaultSections;
      let resultEntries = result.values;

      if (result.currentGroup != null) {
        let { current = [], others = [] } = groupBy(result.values, (item) =>
          item.group === result.currentGroup ? 'current' : 'others'
        );

        if (current.length > 0) {
          sections = [QuickCommandService.CurrentSection, ...sections];

          current = current.map((item) => {
            return {
              ...item,
              section: 'current',
            };
          });

          resultEntries = current.concat(others);
        }
      }

      if (result.showHistory) {
        sections = [QuickCommandService.HistorySection, ...sections];

        const historyName = this.getHistoryName(command, activeResults);
        const historicEntries = this.recentlyUsedService
          .list<CommandResultEntry>(historyName)
          .map((entry) => {
            return {
              ...entry.meta,
              section: 'history',
            };
          });

        const sorted = this.recentlyUsedService.sort<CommandResultEntry>({
          compare: (item, id) => item.historyId === id,
          historyName,
          items: historicEntries,
          order: 'last-used',
        });

        resultEntries = [...sorted, ...resultEntries];
      }

      // If the command returns multiple results without
      // explicitly defining `replace`, we assume that the
      // subsequent results should replace the current one.
      if (firstResult === false && result.replace == null) {
        result.replace = true;
      }

      return {
        currentGroup: result.currentGroup,
        debounce: result.debounce,
        emptyMessage: result.emptyMessage,
        errorMessage: result.errorMessage,
        filterAutomatically: result.filterAutomatically ?? commandDefinition.filter === 'automatic',
        highlight: result.highlight ?? true,
        infoMessage: result.infoMessage,
        placeholder: result.placeholder,
        readonly: result.readonly ?? false,
        rememberResult: result.rememberResult ?? false,
        replace: result.replace ?? false,
        results: resultEntries.map((result) => {
          return {
            ...result,
          };
        }),
        sections: sections ?? this.defaultSections,
        showHistory: result.showHistory ?? false,
        sort: result.sort,
        statusbar: this.normalizeStatusbarResult(result),
        type: result.type,
        warningMessage: result.warningMessage,
      };
    } else {
      return {
        currentGroup: null,
        debounce: false,
        emptyMessage: null,
        errorMessage: null,
        filterAutomatically: false,
        highlight: false,
        infoMessage: null,
        placeholder: null,
        refresh: result.type === 'return' ? result.refresh : false,
        rememberResult: false,
        replace: false,
        results: [],
        sections: [],
        readonly: false,
        showHistory: false,
        statusbar: null,
        type: result.type,
        warningMessage: null,
      };
    }
  }

  private getCommands(commands: CommandDefinition<unknown>[]): Command[] {
    return commands.map((command): Command => {
      const label = this.getLabel(command);

      return {
        alwaysShowSections: command.alwaysShowSections ?? false,
        countInvocations: command.countInvocations ?? true,
        fallback: command.fallback ?? false,
        historyCount: command.historyCount,
        hotkey: command.hotkey,
        icon: command.icon,
        label,
        name: command.name,
        section: command.section?.name ?? 'others',
        template: command.template,
      };
    });
  }

  private getHistoryName(command: Command | string, activeResults: (CommandResultEntry | string)[]): string {
    const name = typeof command === 'string' ? command : command.name;
    const historyIds = activeResults.map((result) =>
      typeof result === 'string' ? result : result.historyId
    );

    const key = [name, ...historyIds].filter((id) => id != null).join('-');

    return `quick-command-${key}`;
  }

  private getSections(commands: CommandDefinition<unknown>[]): CommandSection[] {
    return commands
      .map((command) => command.section)
      .filter((section) => section != null)
      .reduce((a, b) => {
        if (b == null) {
          return a;
        }

        const maybeSection = a.find((section) => section.name === b.name);

        if (maybeSection != null) {
          // Sanity check
          if (maybeSection.label !== b.label) {
            console.warn(
              `Found two sections with same name but different labels. a=${JSON.stringify(a, null, 2)}, b=${JSON.stringify(b, null, 2)}`
            );
          }

          return a;
        } else {
          return [...a, b];
        }
      }, this.defaultSections);
  }

  private normalizeStatusbarResult(result: CommandResultList<unknown>): CommandStatusbar | null {
    if (typeof result.statusbar === 'string') {
      return {
        translationKey: result.statusbar,
      };
    } else {
      return result.statusbar;
    }
  }

  private rememberCommandResult(
    command: Command,
    selectedResults: EnhancedCommandResultEntry[],
    chosenEntry: EnhancedCommandResultEntry
  ): void {
    const historyName = this.getHistoryName(command, selectedResults);

    const { command: _, ...entryWithoutCommand } = chosenEntry;

    this.recentlyUsedService.add({
      historyName: historyName,
      id: chosenEntry.historyId!,
      max: command.historyCount,
      meta: entryWithoutCommand,
    });
  }
}

function wrap<T>(valueOrObservable: T | Observable<T>): Observable<T> {
  if (isObservable(valueOrObservable)) {
    return valueOrObservable;
  } else {
    return of(valueOrObservable);
  }
}

interface CallCommandOptions {
  /**
   * String that the user entered to filter results.
   */
  query?: string;

  /**
   * Remember the latest result.
   */
  remember: boolean;

  /**
   * Active results selected by the user.
   */
  results: EnhancedCommandResultEntry[];
}
